import { useState, type ElementType } from "react";
import {
  Box,
  Stack,
  Typography,
  ButtonBase,
  Chip,
  Divider
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import ReceiptLongRounded from "@mui/icons-material/ReceiptLongRounded";
import AddShoppingCartRounded from "@mui/icons-material/AddShoppingCartRounded";
import SellRounded from "@mui/icons-material/SellRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";

import type { Position } from "@/data/mock-positions";
import { formatUSD } from "@/lib/formatters";
import { TickerAvatar } from "@/components/TickerAvatar";
import { useSwipeReveal } from "@/hooks/useSwipeReveal";
import { ItemActionSheet } from "@/components/ItemActionSheet";
import type { ItemAction } from "@/components/ItemActionSheet";

// ---- Public component -----------------------------------------------------

interface PositionsSimpleViewProps {
  /** Already filtered + sorted by the caller. */
  positions: Position[];
  /**
   * Click handler — opens the position detail drawer (PositionsPanel
   * hosts the drawer; this view just signals which row was tapped).
   * Same shape as BotsSimpleView's `onBotClick`.
   */
  onPositionClick?: (id: string) => void;
  /**
   * Optional per-position weekly $/% delta. Used as the fallback when
   * today's change is 0 (e.g. weekends, holidays, or the broker
   * snapshot hasn't yet caught a price tick). Same shape + semantics
   * as BotsSimpleView's `weeklyDeltas`. Keyed by `position.id`.
   */
  weeklyDeltas?: Record<string, { fraction: number; delta: number }>;
}

/**
 * Mobile positions list — minimal, post-Apr-2026 strip-down to match
 * the Bots / Orders simple views.
 *
 * Design brief (Sergey 2026-04-26 follow-on):
 *  • No portfolio summary card — duplicates the AccountsPageV2 hero
 *    above the tab strip and the per-tab summary on the Advanced view.
 *  • Each row: ticker logo + symbol + qty/name caption on the left,
 *    market value + today's ▲/▼ delta on the right. No sparkline in
 *    the row — the drawer keeps a taller version (matches Bots).
 *  • Flat rows separated by Dividers; tap opens the position detail
 *    drawer hosted by the parent panel. Long-swipe reveals the
 *    action sheet (View orders, Buy more, Sell, Close).
 */
export function PositionsSimpleView({
  positions,
  onPositionClick,
  weeklyDeltas
}: PositionsSimpleViewProps) {
  const [menuPosition, setMenuPosition] = useState<Position | null>(null);

  if (positions.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="body2">No positions match your filters.</Typography>
      </Box>
    );
  }

  // Action-sheet contents per position. The "Close position" copy
  // switches naturally with side — Buy more/Sell always refers to the
  // natural directional action, and Close always means "flatten".
  const buildPositionActions = (p: Position): ItemAction[] => {
    const isShort = p.side === "short";
    return [
      {
        id: "view_orders",
        label: "View orders",
        description: `Orders for ${p.asset}`,
        icon: <ReceiptLongRounded fontSize="small" />,
        onSelect: () => onPositionClick?.(p.id)
      },
      {
        id: "buy_more",
        label: isShort ? "Add to short" : "Buy more",
        icon: <AddShoppingCartRounded fontSize="small" />,
        onSelect: () => {}
      },
      {
        id: "sell",
        label: isShort ? "Cover partial" : "Sell",
        icon: <SellRounded fontSize="small" />,
        onSelect: () => {}
      },
      {
        id: "close",
        label: "Close position",
        description: `Flatten ${formatQty(p.qty)} ${formatShareLabel(p.qty)}`,
        icon: <CloseRounded fontSize="small" />,
        onSelect: () => {},
        destructive: true
      }
    ];
  };

  return (
    <>
      <Box sx={{ pt: 1 }}>
        {positions.map((p, i) => (
          <Box key={p.id}>
            {i > 0 && <Divider />}
            <PositionRow
              position={p}
              weeklyDelta={weeklyDeltas?.[p.id]}
              onClick={onPositionClick ? () => onPositionClick(p.id) : undefined}
              onSwipeOpen={setMenuPosition}
            />
          </Box>
        ))}
      </Box>

      <ItemActionSheet
        open={menuPosition !== null}
        onClose={() => setMenuPosition(null)}
        title={
          menuPosition
            ? `${menuPosition.asset} · ${menuPosition.side === "short" ? "Short" : "Long"}`
            : ""
        }
        subtitle={
          menuPosition
            ? `${formatQty(menuPosition.qty)} ${formatShareLabel(menuPosition.qty)} · ${formatUSD(menuPosition.marketValue)}`
            : undefined
        }
        actions={menuPosition ? buildPositionActions(menuPosition) : []}
      />
    </>
  );
}

// ---- Position row --------------------------------------------------------

function PositionRow({
  position,
  weeklyDelta,
  onClick,
  onSwipeOpen
}: {
  position: Position;
  weeklyDelta?: { fraction: number; delta: number };
  onClick?: () => void;
  onSwipeOpen?: (position: Position) => void;
}) {
  // Today / This week / — fallback chain — same logic BotsSimpleView
  // uses (Sergey 2026-04-26 / 27 polish). Real positions where the
  // broker hasn't reported a price tick yet (early morning, weekends,
  // pre-market) fall through to "This week" computed from the last
  // pre-Monday close in the historical-prices map.
  const hasToday =
    Number.isFinite(position.todayPLDollar) && position.todayPLDollar !== 0;
  const periodMode: "today" | "week" | "none" = hasToday
    ? "today"
    : weeklyDelta
      ? "week"
      : "none";
  const periodLabel = periodMode === "week" ? "This week" : "Today";
  const periodFraction =
    periodMode === "today"
      ? position.todayPLPct
      : periodMode === "week"
        ? weeklyDelta!.fraction
        : 0;
  const periodDelta =
    periodMode === "today"
      ? position.todayPLDollar
      : periodMode === "week"
        ? weeklyDelta!.delta
        : 0;
  const periodPositive = periodFraction >= 0;
  const totalPositive = position.totalPLDollar >= 0;

  const Wrapper: ElementType = onClick ? ButtonBase : Box;

  // Swipe-to-reveal the action sheet. Same hook + suppressor pattern
  // BotsSimpleView uses so the tap that ends a qualifying swipe
  // doesn't also trigger the row's tap-to-open onClick.
  const { handlers, translateX, consumeClickSuppression } = useSwipeReveal({
    onSwipe: () => onSwipeOpen?.(position)
  });

  const handleClick = onClick
    ? () => {
        if (consumeClickSuppression()) return;
        onClick();
      }
    : undefined;

  return (
    <Wrapper
      {...(handleClick ? { onClick: handleClick } : {})}
      {...handlers}
      sx={{
        display: "block",
        width: "100%",
        textAlign: "left",
        // Same vertical rhythm as BotsSimpleView's flat row.
        py: 1.25,
        px: 0,
        touchAction: "pan-y",
        transform: `translateX(${translateX}px)`,
        transition:
          translateX === 0
            ? "background-color 120ms ease, transform 180ms ease"
            : "background-color 120ms ease",
        "&:hover": onClick
          ? {
              bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.04)
            }
          : undefined
      }}
    >
      {/* Header row: ticker logo + ticker (+ Short chip) + market value
       *  on a single baseline. Mirrors BotsSimpleView's identity row so
       *  Positions and Bots feel like one product. */}
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <TickerAvatar symbol={position.asset} size={36} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                letterSpacing: 0.2
              }}
            >
              {position.asset}
            </Typography>
            {position.side === "short" && (
              <Chip
                label="Short"
                size="small"
                sx={(t) => ({
                  height: 18,
                  bgcolor: alpha(t.palette.error.main, 0.15),
                  color: t.palette.error.main,
                  fontWeight: 600,
                  "& .MuiChip-label": { px: 0.75, fontSize: 11 }
                })}
              />
            )}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {formatQty(position.qty)} {formatShareLabel(position.qty)}
            {position.assetName ? ` · ${position.assetName}` : ""}
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{
            flexShrink: 0,
            fontWeight: 700,
            fontFeatureSettings: '"tnum" 1',
            lineHeight: 1.2,
            whiteSpace: "nowrap"
          }}
        >
          {formatUSDSmart(position.marketValue)}
        </Typography>
      </Stack>

      {/* Numbers row — same 1fr 1fr grid Bots uses (NOT space-between
       *  — leaves a big empty channel on wide phones). Today on the
       *  left, Total return on the right. Drawer carries the full
       *  detail (avg entry, cost basis, side, opened, etc.). */}
      <Box
        sx={{
          mt: 1,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          columnGap: 2,
          alignItems: "end",
          fontFeatureSettings: '"tnum" 1'
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              display: "block",
              lineHeight: 1.2,
              mb: 0.25
            }}
          >
            {periodLabel}
          </Typography>
          {periodMode === "none" ? (
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontWeight: 700,
                fontFeatureSettings: '"tnum" 1',
                lineHeight: 1.2
              }}
            >
              —
            </Typography>
          ) : (
            <DeltaValueRH
              positive={periodPositive}
              dollar={periodDelta}
              fraction={periodFraction}
            />
          )}
        </Box>

        <Box sx={{ textAlign: "right", minWidth: 0 }}>
          <Typography
            variant="body1"
            sx={{
              color: "transparent",
              display: "block",
              lineHeight: 1.2,
              mb: 0.25,
              userSelect: "none"
            }}
          >
            {/* Spacer matched to left column's "Today" label so dollar
             *  baselines align (same trick BotsSimpleView uses). */}
            &nbsp;
          </Typography>
          <DeltaValueRH
            align="right"
            positive={totalPositive}
            dollar={position.totalPLDollar}
            fraction={position.totalPLPct}
          />
        </Box>
      </Box>
    </Wrapper>
  );
}

/**
 * Robinhood-style signed delta — `▲ $1,234.56 (1.23%)` in green for
 * positive, `▼ $1,234.56 (1.23%)` in red for negative. Triangle
 * conveys direction so we don't repeat a +/− on the dollar value.
 *
 * Identical visual to BotsSimpleView's helper of the same name —
 * keeps the two simple views' delta chips perfectly aligned.
 */
function DeltaValueRH({
  positive,
  dollar,
  fraction,
  align = "left"
}: {
  positive: boolean;
  dollar: number;
  fraction: number;
  align?: "left" | "right";
}) {
  const triangle = positive ? "▲" : "▼";
  return (
    <Stack
      direction="row"
      alignItems="baseline"
      spacing={0.5}
      sx={{
        justifyContent: align === "right" ? "flex-end" : "flex-start",
        fontFeatureSettings: '"tnum" 1',
        flexWrap: "nowrap"
      }}
    >
      <Typography
        component="span"
        variant="body1"
        sx={(t) => ({
          color: positive ? t.palette.success.main : t.palette.error.main,
          fontWeight: 700,
          lineHeight: 1.2,
          flexShrink: 0
        })}
      >
        {triangle}
      </Typography>
      <Typography
        component="span"
        variant="body1"
        sx={(t) => ({
          color: positive ? t.palette.success.main : t.palette.error.main,
          fontWeight: 700,
          fontFeatureSettings: '"tnum" 1',
          lineHeight: 1.2,
          whiteSpace: "nowrap"
        })}
      >
        {formatUSDSmart(Math.abs(dollar))}
      </Typography>
      <Typography
        component="span"
        variant="body1"
        sx={(t) => ({
          color: positive ? t.palette.success.main : t.palette.error.main,
          opacity: 0.85,
          fontWeight: 500,
          fontFeatureSettings: '"tnum" 1',
          lineHeight: 1.2,
          whiteSpace: "nowrap"
        })}
      >
        ({Math.abs(fraction * 100).toFixed(2)}%)
      </Typography>
    </Stack>
  );
}

// ---- Format helpers ------------------------------------------------------

/** Whole numbers render without decimals; fractional qty keeps 4 places. */
function formatQty(n: number): string {
  if (Number.isInteger(n)) {
    return new Intl.NumberFormat("en-US").format(n);
  }
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4
  }).format(n);
}

function formatShareLabel(n: number): string {
  return Math.abs(n) === 1 ? "share" : "shares";
}

/**
 * Drop cents for absolute values > $999.99 to keep row widths tight on
 * larger numbers without sacrificing precision on small balances /
 * deltas. Same helper BotsSimpleView uses (Sergey 2026-04-26).
 */
function formatUSDSmart(n: number): string {
  if (Math.abs(n) > 999.99) {
    return formatUSD(n, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  }
  return formatUSD(n);
}
